// UserService.ts
import { FirebaseService } from "../firebase/firebase-service";
import { Unsubscribe, User, onAuthStateChanged } from "firebase/auth";
import { Timestamp, doc, setDoc, deleteDoc, getDocs, collection, serverTimestamp } from "firebase/firestore";
import {
  USER_ROLES,
  capitalize,
  generateSearchTerms,
  searchFields,
} from "reusable";
import { FIRESTORE_KEYS } from "../keys";
import { auth, db } from "config";
import { Alert, CreatorProfile, LoggedInUser } from "models";
import { getFriendlyErrorMessage } from "../firebase/error-mapping";

export const UserService = {
  async signUpUser(
    email: string,
    password: string,
    name: string,
    handleResponse: (user: User) => void,
    handleError: (alert: Alert) => void,
    additionalData?: Record<string, any>
  ) {
    const userCredential = await FirebaseService.createUserWithEmailAndPass(
      email,
      password
    );
    if ("type" in userCredential) {
      handleError(userCredential as Alert);
      return;
    }
    const user = userCredential;
    const updateProfileResult = await FirebaseService.updateUserProfile(
      user,
      capitalize(name)
    );
    if (updateProfileResult && "type" in updateProfileResult) {
      handleError(updateProfileResult as Alert);
      return;
    }
    const setDocResult = await FirebaseService.setFirestoreDoc(
      FIRESTORE_KEYS.USER_COLLECTION,
      user.uid,
      {
        ...additionalData,
        userId: user.uid,
      }
    );
    if (setDocResult && "type" in setDocResult) {
      handleError(setDocResult as Alert);
      return;
    }
    handleResponse(user);
  },

  async signInWithGoogle(
    handleResponse: (user: User, isNew: boolean) => void,
    handleError: (alert: Alert) => void,
    additionalData?: Record<string, any>
  ) {
    const result = await FirebaseService.signInWithGoogle();
    if ("type" in result) {
      handleError(result as Alert);
      return;
    }
    const user = result;
    const metadata = user.metadata;
    const isNewUser = metadata.creationTime === metadata.lastSignInTime;
    if (isNewUser) {
      // Always create user document for new users with essential fields
      const userDoc = {
        userId: user.uid,
        email: user.email,
        name: capitalize(user.displayName || ""),
        createdAt: new Date().toISOString(),
      };
      const setDocResult = await FirebaseService.setFirestoreDoc(
        FIRESTORE_KEYS.USER_COLLECTION,
        user.uid,
        userDoc
      );
      if (setDocResult && "type" in setDocResult) {
        // Log error and continue
        console.error("Failed to create user document in Firestore", setDocResult);
      }
    }
    handleResponse(user, isNewUser as boolean);
  },

  async loginUser(
    email: string,
    password: string,
    handleResponse: (user: User) => void,
    handleError: (alert: Alert) => void
  ) {
    const userCredential = await FirebaseService.signInWithEmailAndPass(
      email,
      password
    );
    if ("type" in userCredential) {
      handleError(userCredential as Alert);
      return;
    }
    handleResponse(userCredential);
  },

  subscribeToAuthChanges: (
    callback: (user: User | null) => void
  ): Unsubscribe => {
    return onAuthStateChanged(auth, callback);
  },

  async publishUserProfile(
    profileData: CreatorProfile,
    handleSuccess: (profileData: CreatorProfile) => void,
    handleError: (alert: Alert) => void,
    isEditing?: boolean,
    photoLimit?: number
  ) {
    // Whether creating a new profile or updating an existing one,
    // we'll always update the creationTime in the summary document
    // to ensure newly updated profiles appear at the top of the explore list
    
    //first level checking for user id
    if (!profileData?.userId) {
      handleError({
        message: getFriendlyErrorMessage(""),
        type: "error",
      });
      return;
    }

    // --- Parallel Image Uploads ---
    const uploadTasks = [];
    if (profileData?.newCreatorImages?.length) {
      uploadTasks.push(
        FirebaseService.uploadFilesAndRetrieveURLs(
          profileData.userId,
          profileData?.newCreatorImages
        )
      );
    } else {
      uploadTasks.push(Promise.resolve([]));
    }
    if (profileData?.newProfilePicture?.length) {
      uploadTasks.push(
        FirebaseService.uploadFilesAndRetrieveURLs(
          profileData.userId,
          profileData?.newProfilePicture
        )
      );
    } else {
      uploadTasks.push(Promise.resolve([profileData?.profilePicture || ""]));
    }

    let [newCreatorPhotoUrls, newProfilePictureUrls] = await Promise.all(uploadTasks);
    if (newCreatorPhotoUrls && typeof newCreatorPhotoUrls === "object" && "type" in newCreatorPhotoUrls) {
      handleError(newCreatorPhotoUrls as Alert);
      return;
    }
    if (newProfilePictureUrls && typeof newProfilePictureUrls === "object" && "type" in newProfilePictureUrls) {
      handleError(newProfilePictureUrls as Alert);
      return;
    }
    // Use first profile picture URL if available
    let profilePictureUrl = Array.isArray(newProfilePictureUrls) ? newProfilePictureUrls[0] : newProfilePictureUrls;

    // Build unique, filtered creatorPhotos array
    const removedUrlsArr = Array.isArray(profileData?.removedUrls) ? profileData.removedUrls : [];
    const newPhotoUrlsArr = Array.isArray(newCreatorPhotoUrls) ? newCreatorPhotoUrls : [];
    // Filter out blob URLs from existing photos - only keep valid Firebase URLs
    const oldPhotoUrlsArr = Array.isArray(profileData?.creatorPhotos) 
      ? profileData.creatorPhotos.filter((url: string) => 
          typeof url === "string" && url && url.includes("firebasestorage") && !url.startsWith("blob:")
        )
      : [];

    // Debug logging to help track image processing
    console.log('[ProfileUpdate] Image processing:', {
      newUploads: newPhotoUrlsArr.length,
      existingPhotos: oldPhotoUrlsArr.length,
      removedUrls: removedUrlsArr.length,
      hasImageChanges: newPhotoUrlsArr.length > 0 || removedUrlsArr.length > 0
    });

    let allPhotoUrls = [];
    
    // Only process images if there are new uploads or removals
    if (newPhotoUrlsArr.length > 0 || removedUrlsArr.length > 0) {
      // Combine new and existing photos, then filter
      allPhotoUrls = [...newPhotoUrlsArr, ...oldPhotoUrlsArr].flat();
      allPhotoUrls = allPhotoUrls.filter(
        (url) => typeof url === "string" && url && !removedUrlsArr.includes(url)
      );
      // Only keep valid firebase storage URLs and ensure uniqueness
      allPhotoUrls = allPhotoUrls.filter((url, idx, arr) =>
        url.includes("firebasestorage") && arr.indexOf(url) === idx
      );
      // Enforce dynamic photo limit based on subscription
      const maxPhotos = photoLimit || 8; // Default to 8 if not provided
      allPhotoUrls = allPhotoUrls.slice(0, maxPhotos);
      console.log(`[ProfileUpdate] Image changes detected - processed photos: ${allPhotoUrls.length}/${maxPhotos}`);
    } else {
      // No image changes - keep existing photos as is (already filtered above)
      allPhotoUrls = oldPhotoUrlsArr;
      console.log('[ProfileUpdate] No image changes - preserving existing photos:', allPhotoUrls.length);
    }
    profileData.creatorPhotos = allPhotoUrls;
    profileData.profilePicture = profilePictureUrl;

    const { newCreatorImages, newProfilePicture, removedUrls, ...finalData } = profileData;

    // --- Parallel Firestore Writes ---
    // Removed unused summary variable

    const summaryDoc = {
      searchTerms: generateSearchTerms(searchFields, {
        ...profileData,
        location: profileData?.location?.place,
      }),
      creationTime: Timestamp.fromDate(new Date()), // Always update the creationTime on create or update to sort by latest
      lastUpdated: new Date().toISOString(),
      userId: profileData.userId,
      name: profileData.name,
      occupation: profileData.occupation,
      position: profileData.position,
      location: profileData.location,
      profilePicture: profileData.profilePicture,
      //isDeactivated: profileData.isDeactivated ?? false,
      isSubscribed: profileData.isSubscribed ?? false,
      //isVisible: profileData.isVisible ?? true,
      ratings: profileData.ratings ?? {
        overall: 0,
        service: 0,
        design: 0,
        delivery: 0,
        totalRatings: 0,
      },
    };

    // Only send safe, required fields for user update
    const userDoc = {
      isCreatorQuestionsAnswered: true,
      role: USER_ROLES.CREATOR,
      profilePicture: profilePictureUrl,
      contactNumber: profileData?.contactNumber,
      occupation: profileData?.occupation,
      location: profileData?.location,
      name: profileData?.name,
    };

    // Use setDoc with merge for user update
    const [setDocResult, setSummaryResult, userUpdateResult] = await Promise.all([
      FirebaseService.setFirestoreDoc(
        FIRESTORE_KEYS.CREATOR_PROFILES_COLLECTION,
        profileData.userId,
        finalData
      ),
      FirebaseService.setFirestoreDoc(
        FIRESTORE_KEYS.CREATOR_PROFILES_SUMMARY,
        profileData.userId,
        summaryDoc
      ),
      FirebaseService.setFirestoreDoc(
        FIRESTORE_KEYS.USER_COLLECTION,
        profileData.userId,
        userDoc
      ),
    ]);

    if (setDocResult && "type" in setDocResult) {
      handleError(setDocResult as Alert);
      return;
    }
    if (setSummaryResult && "type" in setSummaryResult) {
      handleError(setSummaryResult as Alert);
      return;
    }
    if (userUpdateResult && "type" in userUpdateResult) {
      handleError(userUpdateResult as Alert);
      return;
    }
    handleSuccess(profileData);
  },

  async getLoggedInUser(
    userId: string,
    handleResponse: (user: LoggedInUser) => void,
    handleError: (alert: Alert) => void
  ) {
    const [userRaw, activeSubscription] = await Promise.all([
      FirebaseService.getFirestoreDoc(FIRESTORE_KEYS.USER_COLLECTION, userId),
      FirebaseService.getActiveSubscription(userId)
    ]);
    const user = userRaw as LoggedInUser;

    if ("type" in user) {
      if ("message" in user) {
        handleError(user as Alert);
      } else {
        handleError({ type: "error", message: "Unknown error" } as Alert);
      }
      return;
    }

    // Only set premium if activeSubscription exists
    let updatedUser: LoggedInUser;
    if (activeSubscription) {
      if (activeSubscription.status === 'active') {
        // Safeguard: Only update if user's subscription is not already active and matches subscription data
        const alreadyActive = user.subscription?.active === true && user.subscription?.status === 'active' && user.subscription?.endDate === activeSubscription.endDate;
        updatedUser = Object.assign({}, user, {
          subscription: {
            ...user.subscription,
            active: true,
            aboutToEnd: false,
            status: activeSubscription.status,
            endDate: activeSubscription.endDate || user.subscription?.endDate || null
          }
        });
        if (!alreadyActive) {
          const subscriptionUpdate = {
            active: true,
            status: activeSubscription.status,
            ...(activeSubscription.endDate && { endDate: activeSubscription.endDate })
          };
          // Update user subscription status
          await FirebaseService.updateFirestoreDoc(
            FIRESTORE_KEYS.USER_COLLECTION,
            userId,
            { subscription: subscriptionUpdate }
          );
          
          // CRITICAL: Also update creator profiles with isSubscribed flag for premium listing priority
          await Promise.all([
            FirebaseService.updateFirestoreDoc(
              FIRESTORE_KEYS.CREATOR_PROFILES_COLLECTION,
              userId,
              { isSubscribed: true, lastSubscriptionUpdate: new Date().toISOString() }
            ),
            FirebaseService.updateFirestoreDoc(
              FIRESTORE_KEYS.CREATOR_PROFILES_SUMMARY,
              userId,
              { isSubscribed: true, lastSubscriptionUpdate: new Date().toISOString() }
            )
          ]).catch((error) => {
            console.warn("Failed to sync premium status to creator profiles on login:", error);
          });
        }
      } else {
        updatedUser = Object.assign({}, user, {
          subscription: {
            ...user.subscription,
            active: false,
            aboutToEnd: false,
            status: activeSubscription.status,
            endDate: activeSubscription.endDate || null
          }
        });
        // Update the user document if needed
        if (user.subscription?.active !== false || user.subscription?.status !== activeSubscription.status) {
          await FirebaseService.updateFirestoreDoc(
            FIRESTORE_KEYS.USER_COLLECTION,
            userId,
            {
              subscription: {
                active: false,
                status: activeSubscription.status,
                endDate: activeSubscription.endDate || null
              }
            }
          );
          
          // Also update creator profiles to remove premium flag when subscription is inactive
          await Promise.all([
            FirebaseService.updateFirestoreDoc(
              FIRESTORE_KEYS.CREATOR_PROFILES_COLLECTION,
              userId,
              { isSubscribed: false, lastSubscriptionUpdate: new Date().toISOString() }
            ),
            FirebaseService.updateFirestoreDoc(
              FIRESTORE_KEYS.CREATOR_PROFILES_SUMMARY,
              userId,
              { isSubscribed: false, lastSubscriptionUpdate: new Date().toISOString() }
            )
          ]).catch((error) => {
            console.warn("Failed to sync premium status to creator profiles on login:", error);
          });
        }
      }
    } else {
      updatedUser = Object.assign({}, user, {
        subscription: {
          ...user.subscription,
          active: false,
          aboutToEnd: false,
          status: 'inactive',
          endDate: null
        }
      });
      // Update the user document if needed
      if (user.subscription?.active !== false || user.subscription?.status !== 'inactive') {
        await FirebaseService.updateFirestoreDoc(
          FIRESTORE_KEYS.USER_COLLECTION,
          userId,
          {
            subscription: {
              active: false,
              status: 'inactive',
              endDate: null
            }
          }
        );
      }
    }

    // Keep creator profile summary in sync for premium ordering
    try {
      await FirebaseService.updateFirestoreDoc(
        FIRESTORE_KEYS.CREATOR_PROFILES_COLLECTION,
        userId,
        { isSubscribed: Boolean(updatedUser?.subscription?.active) }
      );
      await FirebaseService.updateFirestoreDoc(
        FIRESTORE_KEYS.CREATOR_PROFILES_SUMMARY,
        userId,
        { isSubscribed: Boolean(updatedUser?.subscription?.active) }
      );
    } catch (error) {
      console.warn("Failed to sync premium status to summary:", error);
    }

    handleResponse(updatedUser);
  },

  async sendPasswordResetEmail(
    email: string,
    handleSuccess: () => void,
    handleError: (alert: Alert) => void
  ) {
    const result = await FirebaseService.sendPasswordReset(email);
    if (result.type === "success") {
      handleSuccess();
    } else {
      handleError(result as Alert);
    }
  },

  async followUser(currentUserId: string, targetUserId: string) {
    try {
      // 1. Add targetUserId to currentUserId's following subcollection
      const followingRef = doc(db, FIRESTORE_KEYS.USER_COLLECTION, currentUserId, "following", targetUserId);
      await setDoc(followingRef, {
        timestamp: serverTimestamp()
      });

      // 2. Add currentUserId to targetUserId's followers subcollection
      const followersRef = doc(db, FIRESTORE_KEYS.USER_COLLECTION, targetUserId, "followers", currentUserId);
      await setDoc(followersRef, {
        timestamp: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error("Error following user:", error);
      return false;
    }
  },

  async unfollowUser(currentUserId: string, targetUserId: string) {
    try {
      // 1. Remove targetUserId from currentUserId's following subcollection
      const followingRef = doc(db, FIRESTORE_KEYS.USER_COLLECTION, currentUserId, "following", targetUserId);
      await deleteDoc(followingRef);

      // 2. Remove currentUserId from targetUserId's followers subcollection
      const followersRef = doc(db, FIRESTORE_KEYS.USER_COLLECTION, targetUserId, "followers", currentUserId);
      await deleteDoc(followersRef);
      
      return true;
    } catch (error) {
      console.error("Error unfollowing user:", error);
      return false;
    }
  },

  async getFollowingIds(userId: string): Promise<string[]> {
    try {
      const followingRef = collection(db, FIRESTORE_KEYS.USER_COLLECTION, userId, "following");
      const snapshot = await getDocs(followingRef);
      return snapshot.docs.map(doc => doc.id);
    } catch (error) {
      console.error("Error fetching following ids:", error);
      return [];
    }
  },

  async logout(callback: () => void, handleError: (alert: Alert) => void) {
    try {
      await auth.signOut();
      callback();
    } catch (error) {
      handleError({ message: "Logout failed.", type: "error" });
    }
  },
};
